import classNames from "classnames";
import React, {useEffect, useState} from "react";
import {ColumnDescription} from "react-bootstrap-table-next";
import {useTranslation} from "react-i18next";
import {SortOrder} from "../../../shared/enums/sortOrder";
import NoticesDataListItem from "../../../stores/domainStore/pages/models/noticesDataListItem";
import Tag, {TagType} from "../../atoms/tag/tag";
import PaginatedDataTable from "../../molecules/paginatedDataTable/paginatedDataTable";
import "./noticesDataList.sass";
import {NoticeStatus} from "../../../shared/enums/noticeStatus";
import {OptionMenuKeyValue} from "../../../stores/observables/optionMenuKeyValue";
import OptionMenu from "../../atoms/optionMenu/optionMenu";
import {limitLengthWithEllipsis} from "../../../utils/stringUtils";
import {
  allVillageKeyValue,
  excludeHeightForNotices,
  heightOfDataRow,
  multiVillageKeyValue,
  ServicedApartmentResidentsFeatureName
} from "../../../shared/constants";
import {useVillageFeatureToggle} from "../../../utils/featureToggle/villageFeatureToggle";
import useWindowDimensions from "../../../utils/ui/useWindowDimensions";
import {ObservableList} from "../../../stores/observables/observableList";
import Icon from "../../atoms/common/icon/icon";
import {IconSource} from "../../../shared/enums/iconSource";
import {format} from "date-fns";
import {MultiVillageHelper} from "../../../shared/multiVillageHelper";
import {AuthUser} from "../../../utils/auth/user";
import {useStores} from "../../../stores/useStores";
import AreaTag from "../../atoms/areaTag/areaTag";

export interface INoticesDataListProps {
  /**
   * Notices to be displayed on the data table.
   */
  notices: NoticesDataListItem[];

  /**
   * Indicating that it is currently navigating to another page.
   */
  isNavigating: boolean;

  /**
   * Indicating that the notices are loading.
   */
  isLoading: boolean;

  /**
   * The event callback for the navigation to the previous page.
   */
  onPrevPage?: (pageNumber: number, pageSize: number) => void;

  /**
   * The event callback for the navigation to the next page.
   */
  onNextPage?: (pageNumber: number, pageSize: number) => void;

  /**
   * The event callback for the navigation to a specific page.
   */
  onPageNumberSelected?: (pageNumber: number, pageSize: number) => void;

  /**
   * The length of notices.
   */
  noticesLength: number;

  /**
   * The callback for notices list sort event.
   * If it is not provided, sorting will be based on the existing data.
   */
  onSort?: (
    sortField: string,
    sortOrder: SortOrder,
    pageNumber: number,
    sizePerPage: number
  ) => void;

  /**
   * The callback for notices list page change event.
   * If it is not provided, page change will be based on the existing data.
   */
  onPageChange?: (
    pageNumber: number,
    sizePerPage: number,
    sortField: string,
    sortOrder: SortOrder
  ) => void;

  /**
   * The callback for notices list notice selected event.
   */
  onNoticeSelected: (noticeId: string) => void;

  /**
   * The page number that should be displayed by the table currently. This is mainly to trigger UI rendering, since the data are paginated.
   */
  currentPageNumber?: number;

  /**
   * The field that should be sorted by the table currently. This is mainly to trigger UI rendering, since the data are paginated.
   */
  currentSortField?: string;

  /**
   * The order that should be sorted by the table currently. This is mainly to trigger UI rendering, since the data are paginated.
   */
  currentSortOrder?: SortOrder;

  /**
   * A list of actions that can be applied to the selected notice.
   * +
   */
  actions: OptionMenuKeyValue[];

  /**
   * Action to be applied to the selected notice.
   * @param action
   */
  onActionSelected: (action: OptionMenuKeyValue) => void;

  /**
   * The selected notice to apply an action to.
   */
  selectedNotice: NoticesDataListItem;

  /**
   * Filter function to filter available actions for the selected notice.
   */
  actionFilterFn?: (
    items: OptionMenuKeyValue[],
    notice: NoticesDataListItem
  ) => OptionMenuKeyValue[];
}

const NoticesDataList: React.FC<INoticesDataListProps> = ({
  notices,
  isNavigating,
  isLoading,
  onPrevPage,
  onNextPage,
  onPageNumberSelected,
  noticesLength,
  onPageChange,
  onSort,
  onNoticeSelected,
  currentPageNumber,
  currentSortField = "",
  currentSortOrder = SortOrder.Ascending,
  actions,
  onActionSelected,
  selectedNotice,
  actionFilterFn
}) => {
  const {uiStore, domainStore} = useStores();
  const hasServicedApartmentResidents = useVillageFeatureToggle(
    ServicedApartmentResidentsFeatureName
  ).hasFeature.value;
  const isMultiVillageSelected =
    uiStore.selectableVillages.selectedItem?.value ===
    multiVillageKeyValue.value;
  const [sortField, setSortField] = useState(currentSortField);
  const [sortOrder, setSortOrder] = useState(currentSortOrder);
  const {t} = useTranslation(["notice"]);
  const {height} = useWindowDimensions();
  const sizePerPage = Math.round(
    (height - excludeHeightForNotices) / heightOfDataRow
  );
  const canSort = notices.length > 0;

  useEffect(() => {
    setSortField(currentSortField);
  }, [currentSortField]);

  useEffect(() => {
    setSortOrder(currentSortOrder);
  }, [currentSortOrder]);

  const sortHandler = (
    sortField: string,
    sortOrder: SortOrder,
    pageNumber: number,
    sizePerPage: number
  ): void => {
    setSortField(sortField);
    setSortOrder(sortOrder);
    onSort?.(sortField, sortOrder, pageNumber, sizePerPage);
  };

  const headerLabelFormatter = (column: ColumnDescription<any, any>) => {
    const isColumnSorting = sortField === column.dataField;
    const isSortAscending = sortOrder === SortOrder.Ascending;
    return (
      <div
        className={classNames("notices-table-sortable-header", {
          "is-sort": isColumnSorting
        })}
      >
        {column.text}
        {canSort && (
          <div>
            {isColumnSorting && !isSortAscending && (
              <Icon source={IconSource.toggleUp} />
            )}
            {isColumnSorting && isSortAscending && (
              <Icon source={IconSource.toggleDown} />
            )}
            {!isColumnSorting && <Icon source={IconSource.toggleBoth} />}
          </div>
        )}
      </div>
    );
  };

  const iconHeaderLabelFormatter = () => {
    return (
      <div>
        <Icon source={IconSource.notification} />
      </div>
    );
  };

  const statusFormatter = (status: NoticeStatus) => {
    let tagType: TagType;
    switch (status) {
      case NoticeStatus.Published:
        tagType = "success";
        break;
      case NoticeStatus.Draft:
        tagType = "system";
        break;
      case NoticeStatus.Scheduled:
        tagType = "warning";
        break;
      default:
        tagType = "neutral";
        break;
    }
    return (
      <div>
        <Tag text={status} type={tagType} />
      </div>
    );
  };

  const notificationEnabledFormatter = (
    notificationEnabled: boolean,
    notice: NoticesDataListItem
  ) => (
    <div>
      {notificationEnabled && notice.status !== NoticeStatus.Draft ? (
        <Icon source={IconSource.notification} />
      ) : (
        <></>
      )}
    </div>
  );

  const optionMenuFormatter = (_: any, notice: NoticesDataListItem) => {
    const filteredActions = actionFilterFn
      ? actionFilterFn(actions, notice)
      : actions;
    return (
      <OptionMenu
        options={new ObservableList<OptionMenuKeyValue>(filteredActions)}
        onOptionSelected={(selectedObservableItem: OptionMenuKeyValue) => {
          selectedNotice.id = notice.id;
          selectedNotice.notice = notice.notice;
          onActionSelected(selectedObservableItem);
        }}
        disabled={!filteredActions.length}
      />
    );
  };

  const noticeFormatter = (notice: string, {id}: {id: string}) => (
    <div
      className="notice-col"
      onClick={(_) => {
        onNoticeSelected(id);
      }}
    >
      {notice && notice.trim() !== ""
        ? notice
        : t("notice:titles.untitledNotice")}
    </div>
  );

  // TODO: Need to revise the publishDateFormatter when we are supporting more localities.
  // publishDate will be provided as string.
  const publishDateFormatter = (publishDate: string) => {
    if (!publishDate) {
      return "-";
    }
    return format(new Date(publishDate), "dd/MM/yyyy");
  };

  const authorFormatter = (author: string) => {
    return !author ? "-" : limitLengthWithEllipsis(author, 30);
  };

  const audienceFormatter = (_: any, notice: NoticesDataListItem) => {
    const {personTypesDomainStore} = domainStore;
    const personTypes = notice.personTypes;
    if (personTypes?.length === 0) return "-";

    const showServiced =
      !!personTypes &&
      personTypes.some((id) => personTypesDomainStore.isServiced(id));
    // Independent-only/legacy notices persist no person types.
    const showIndependent =
      !personTypes ||
      personTypes.some((id) => personTypesDomainStore.isIndependent(id));
    return (
      <div className="audience-tags">
        {showIndependent && (
          <AreaTag
            personTypeId={personTypesDomainStore.independentPersonTypeId}
          />
        )}
        {showServiced && (
          <AreaTag personTypeId={personTypesDomainStore.servicedPersonTypeId} />
        )}
      </div>
    );
  };

  const readCountFormatter = (
    readCount: number | null,
    notice: NoticesDataListItem
  ) => {
    const isDraft =
      notice.status === NoticeStatus.Draft ||
      notice.status === NoticeStatus.Scheduled;
    return (
      <div className={classNames("view-count", {disabled: isDraft})}>
        <Icon source={IconSource.show} />
        {isDraft ? "" : readCount ?? 0}
      </div>
    );
  };

  const villageMap = new Map(
    AuthUser.getInstance().Villages?.map((v) => [v.id, v.name])
  );

  function villageFormatter(villageIds: string[]) {
    if (!villageIds) return allVillageKeyValue.label;
    if (villageIds.length === 0) return "-"; // no village
    const villages = villageIds.map((vId) => villageMap.get(vId)!);
    return MultiVillageHelper.VillageEllipsis(t, villages, true, 3);
  }

  const updateTypeFormatter = (_: any, notice: NoticesDataListItem) => {
    // If the status is Draft, we do not show any icons - Type and Notification.
    if (notice.status === NoticeStatus.Draft) {
      return <></>;
    }

    if (notice.isAnnouncement) {
      return (
        <Icon id="updates-announcement" source={IconSource.announcement} />
      );
    }

    if (notice.isImportant) {
      return <Icon id="updates-important" source={IconSource.info} />;
    }

    return <Icon id="updates-general" source={IconSource.general} />;
  };

  const columns: ColumnDescription<any, any>[] = [
    {
      dataField: "id",
      hidden: true,
      text: "Id"
    },
    {
      dataField: "status",
      text: t("notice:labels.status"),
      classes: "status-col",
      sort: canSort,
      headerFormatter: headerLabelFormatter,
      formatter: statusFormatter
    },
    {
      dataField: "updateType",
      text: "",
      classes: "update-type-col",
      headerClasses: "update-type-header-col",
      sort: false,
      formatter: updateTypeFormatter
    },
    {
      dataField: "notice",
      text: t("notice:labels.title"),
      headerClasses: "title-header",
      sort: canSort,
      classes: "title-col",
      headerFormatter: headerLabelFormatter,
      formatter: noticeFormatter
    },
    {
      dataField: "notificationEnabled",
      text: "bell",
      classes: "notification-col",
      headerClasses: "notification-header-col",
      sort: false,
      headerFormatter: iconHeaderLabelFormatter,
      formatter: notificationEnabledFormatter
    },
    {
      dataField: "author",
      text: t("notice:labels.author"),
      hidden: isMultiVillageSelected,
      sort: canSort,
      headerFormatter: headerLabelFormatter,
      formatter: authorFormatter
    },
    {
      dataField: "villageIds",
      text: t("notice:labels.villages"),
      hidden: !isMultiVillageSelected,
      sort: false,
      formatter: villageFormatter
    },
    {
      dataField: "personTypes",
      text: t("notice:labels.audience"),
      classes: "audience-col",
      hidden: !hasServicedApartmentResidents && !isMultiVillageSelected,
      sort: false,
      formatter: audienceFormatter
    },
    {
      dataField: "readCount",
      text: t("notice:labels.readCount"),
      headerClasses: "read-count-col",
      sort: false,
      formatter: readCountFormatter
    },
    {
      dataField: "publishDate",
      text: t("notice:labels.publishDate"),
      classes: "publish-date-col",
      sort: canSort,
      headerFormatter: headerLabelFormatter,
      formatter: publishDateFormatter
    },
    {
      dataField: "optionMenu",
      text: "",
      sort: false,
      classes: "option-menu-col",
      formatter: optionMenuFormatter
    }
  ];

  const noNoticesIndication = (
    <div className="no-result">
      {isLoading || isNavigating ? "" : t("notice:messages.noNotices")}
    </div>
  );

  return (
    <div className="notices-data-list" data-testid="noticesDataList">
      <PaginatedDataTable
        data={notices}
        keyField="id"
        columns={columns}
        sizePerPage={sizePerPage}
        onPrevPage={onPrevPage}
        onNextPage={onNextPage}
        onPageNumberSelected={onPageNumberSelected}
        noDataIndication={noNoticesIndication}
        dataLength={noticesLength}
        onPageChange={onPageChange}
        onSort={sortHandler}
        remote={true}
        currentPageNumber={currentPageNumber}
      />
    </div>
  );
};

export default NoticesDataList;
